/**
 * Admin view of a specific challenge.
 * Shows all details exactly as the challenge creator would see it,
 * plus additional admin-only controls and statistics.
 */

import { ArrowLeft, Clock, Trophy, Shield, Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SubmissionCard } from '@/components/SubmissionCard';
import { challengeCoverColors, mockChallenges, mockSubmissionsByChallenge } from '@/lib/mockData';
import { useCountdown } from '@/lib/useCountdown';
import { cn } from '@/lib/cn';

type AdminAction = 'none' | 'suspend' | 'remove-submissions';

/**
 * Admin-only page for viewing challenge details.
 * Admins can see:
 * - All submissions (including flagged/removed ones)
 * - All submissions with all statuses
 * - Submission flagging details
 * - Additional admin actions (suspend challenge, bulk remove)
 * - Export challenge data
 *
 * Admins have override power: they can approve or remove ANY submission,
 * regardless of what the creator decided.
 */
export function AdminChallengeViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [adminAction, setAdminAction] = useState<AdminAction>('none');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'most-jems' | 'least-jems'>('newest');

  const challenge = useMemo(() => mockChallenges.find((c) => c.id === id), [id]);

  const submissions = useMemo(() => mockSubmissionsByChallenge[id ?? ''] ?? [], [id]);

  const countdown = useCountdown(challenge?.endsAt ?? new Date().toISOString());
  const coverColor = challengeCoverColors[challenge?.id ?? ''] ?? 'from-slate-400 to-slate-600';

  if (!challenge) {
    return (
      <div className="flex min-h-96 flex-col items-center justify-center text-center">
        <p className="mb-4 text-slate-600">Challenge not found.</p>
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="bg-brand-600 hover:bg-brand-700 rounded-full px-4 py-2 text-sm font-semibold text-white"
        >
          Back to admin dashboard
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="text-brand-600 hover:text-brand-700 flex items-center gap-2 text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </button>
        <div className="flex items-center gap-2 rounded-lg bg-blue-100 px-3 py-2 text-sm font-semibold text-blue-700">
          <Shield className="h-4 w-4" />
          Admin View
        </div>
      </div>

      {/* Admin actions bar */}
      <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <p className="mb-3 font-semibold text-blue-900">Admin Actions</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setAdminAction('suspend')}
            className={cn(
              'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
              adminAction === 'suspend'
                ? 'bg-red-600 text-white'
                : 'border border-red-200 bg-white text-red-600 hover:bg-red-50',
            )}
          >
            Suspend Challenge
          </button>
          <button
            type="button"
            onClick={() => setAdminAction('remove-submissions')}
            className={cn(
              'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
              adminAction === 'remove-submissions'
                ? 'bg-red-600 text-white'
                : 'border border-red-200 bg-white text-red-600 hover:bg-red-50',
            )}
          >
            Remove All Submissions
          </button>
          <button
            type="button"
            className="text-brand-600 border-brand-200 hover:bg-brand-50 flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-medium"
          >
            <Download className="h-4 w-4" />
            Export Data
          </button>
        </div>
      </div>

      {/* Cover section */}
      <div
        className={cn(
          'relative mb-6 overflow-hidden rounded-3xl bg-gradient-to-br p-6 lg:p-8',
          coverColor,
        )}
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.2),_transparent_60%)]" />
        <div className="relative">
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
            <span>{challenge.category}</span>
          </div>
          <h1 className="mb-2 max-w-2xl text-3xl font-bold text-white lg:text-4xl">
            {challenge.title}
          </h1>
          <p className="max-w-xl text-white/90">{challenge.brief}</p>
        </div>
      </div>

      {/* Challenge info bar */}
      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Creator</p>
          <p className="mt-1 font-semibold text-slate-900">{challenge.creator.displayName}</p>
          <p className="mt-1 text-xs text-slate-500">{challenge.creatorId}</p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Submissions</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{submissions.length}</p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Jems</p>
          <p className="text-brand-600 mt-1 text-2xl font-bold">
            {submissions.reduce((sum, s) => sum + s.jemCount, 0)}
          </p>
        </div>

        <div
          className={cn('rounded-xl p-4', countdown.urgent ? 'bg-red-50' : 'bg-white shadow-sm')}
        >
          <p
            className={cn(
              'text-xs font-medium uppercase tracking-wide',
              countdown.urgent ? 'text-red-600' : 'text-slate-500',
            )}
          >
            <Clock className="mb-1 inline h-3 w-3" /> Time Left
          </p>
          <p
            className={cn(
              'mt-1 text-lg font-bold',
              countdown.urgent ? 'text-red-600' : 'text-slate-900',
            )}
          >
            {countdown.label}
          </p>
        </div>
      </div>

      {/* Submissions header + sort */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900">
          {submissions.length} {submissions.length === 1 ? 'Submission' : 'Submissions'}
        </h2>

        {submissions.length > 1 && (
          <select
            value={sortBy}
            onChange={(e) =>
              setSortBy(e.target.value as 'newest' | 'oldest' | 'most-jems' | 'least-jems')
            }
            className="focus:border-brand-400 focus:ring-brand-100 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="most-jems">Most jems</option>
            <option value="least-jems">Least jems</option>
          </select>
        )}
      </div>

      {/* Admin note about submissions */}
      <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
        <p className="font-semibold">Note: Admins see ALL submissions</p>
        <p className="mt-1">
          Including flagged, removed, and pending moderation. You can approve or remove any
          submission regardless of creator action.
        </p>
      </div>

      {/* Submissions grid */}
      {submissions.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center">
          <Trophy className="mx-auto mb-3 h-8 w-8 text-slate-400" />
          <p className="text-slate-500">No submissions.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {submissions.map((s) => (
            <div key={s.id} className="relative">
              <SubmissionCard submission={s} isVoted={false} onVote={() => {}} onFlag={() => {}} />
              {/* Admin action overlay */}
              <div className="absolute inset-0 flex items-end justify-center rounded-2xl bg-black/0 p-4 opacity-0 transition-colors hover:bg-black/30 hover:opacity-100">
                <div className="flex w-full gap-2">
                  <button className="flex-1 rounded-lg bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-700">
                    Approve
                  </button>
                  <button className="flex-1 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700">
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Spacer for mobile bottom nav */}
      <div className="h-8" />
    </>
  );
}
